/**
 * Who is signed in, and where their key is.
 *
 * Two ways in:
 *
 * - **Local key.** Fast, and held in this tab only — never written to disk, so
 *   a refresh signs you out. That cost is deliberate: an archive whose key sits
 *   in localStorage is only as private as the browser profile around it.
 * - **Amber, or any NIP-46 signer.** The key stays on the phone and every
 *   operation is approved there. The browser never sees it at all.
 */
import { generateSecretKey, nip19 } from "nostr-tools";
import { RelayConnection, signerFor } from "./nostr/relay";
import { Allowlist } from "./nostr/contacts";
import { LocalSigner, AmberSigner, type MailSigner } from "./nostr/signer";
import { clearAll } from "./store/db";
import { remember, recall, forget } from "./persist";
import { bytesToHex, hexToBytes } from "./hex";

export interface Session {
  pubkey: string;
  npub: string;
  signer: MailSigner;
  relay: RelayConnection;
  /** The mail relay, which is also where this account receives DMs. */
  relayUrl: string;
  allowlist: Allowlist;
  /** The ingest identity: the other half of the contact list's key. */
  ingestPubkey: string;
  /** The domains this server hosts, which is how the client spots its own address. */
  domains: string[];
}

const RELAY_URL = import.meta.env["VITE_RELAY_URL"] ?? "wss://relay.nicemail.ca";
const MAIL_DOMAINS = (import.meta.env["VITE_MAIL_DOMAINS"] ?? "nicemail.ca,halimail.ca")
  .split(",")
  .map((domain: string) => domain.trim().toLowerCase())
  .filter(Boolean);
const INGEST_PUBKEY =
  import.meta.env["VITE_INGEST_PUBKEY"] ??
  "f79338f652f6adb4c4b07fa2f203fea3803b97cef47851da9ed570e456671ba0";

let current: Session | null = null;
let restoreFailure: string | null = null;

/** A signer that never answers would otherwise hold the boot screen forever. */
const RECONNECT_TIMEOUT_MS = 30_000;

function toSecretBytes(input: string): Uint8Array {
  const trimmed = input.trim();
  if (trimmed.startsWith("nsec")) {
    const decoded = nip19.decode(trimmed);
    if (decoded.type !== "nsec") throw new Error("that is not a private key");
    return decoded.data;
  }
  if (!/^[0-9a-f]{64}$/i.test(trimmed)) {
    throw new Error("expected an nsec or 64 hex characters");
  }
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) bytes[i] = Number.parseInt(trimmed.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

function sessionFor(signer: MailSigner): Session {
  const relay = new RelayConnection({ url: RELAY_URL, pubkey: signer.pubkey }, signerFor(signer));
  current = {
    pubkey: signer.pubkey,
    npub: nip19.npubEncode(signer.pubkey),
    signer,
    relay,
    relayUrl: RELAY_URL,
    allowlist: new Allowlist(),
    ingestPubkey: INGEST_PUBKEY,
    domains: MAIL_DOMAINS,
  };
  return current;
}

export async function signInWithKey(input: string, staySignedIn = false): Promise<Session> {
  const secret = toSecretBytes(input);
  if (staySignedIn) {
    remember({ kind: "local", secret: bytesToHex(secret) });
  }
  return sessionFor(new LocalSigner(secret));
}

/**
 * Connect to a NIP-46 signer from a `bunker://` string.
 *
 * The client half of the connection is a throwaway keypair generated here; it
 * authorises this browser to ask, and nothing more.
 */
export async function signInWithBunker(
  uri: string,
  onAuthUrl?: (url: string) => void,
): Promise<Session> {
  const clientSecret = generateSecretKey();
  const signer = await AmberSigner.connect(uri, clientSecret, onAuthUrl);
  rememberRemote(signer, clientSecret);
  return sessionFor(signer);
}

/**
 * Remember a remote connection.
 *
 * What is stored is the client key this browser generated and the signer's
 * address — permission to ask the phone, never the key itself.
 */
function rememberRemote(signer: AmberSigner, clientSecret: Uint8Array): void {
  const pointer = signer.pointer;
  remember({
    kind: "amber",
    clientSecret: bytesToHex(clientSecret),
    pointer: { pubkey: pointer.pubkey, relays: pointer.relays, secret: pointer.secret },
  });
}

/** Wait for a signer to scan a `nostrconnect://` URI we are advertising. */
export async function signInWithNostrConnect(
  uri: string,
  clientSecret: Uint8Array,
  abort: AbortSignal,
): Promise<Session> {
  const signer = await AmberSigner.fromNostrConnect(uri, clientSecret, abort);
  rememberRemote(signer, clientSecret);
  return sessionFor(signer);
}

/**
 * Bring back the previous session, if there is one.
 *
 * A remote connection is re-established against the signer that already
 * authorised this browser, so no second scan is needed. A failure here is not
 * fatal: the stored connection is dropped and the caller shows sign-in.
 */
export async function restoreSession(): Promise<Session | null> {
  if (current) return current;

  const stored = recall();
  if (!stored) return null;

  try {
    if (stored.kind === "local") {
      return sessionFor(new LocalSigner(hexToBytes(stored.secret)));
    }
    const signer = await withTimeout(
      AmberSigner.reconnect(
        { pubkey: stored.pointer.pubkey, relays: stored.pointer.relays, secret: stored.pointer.secret },
        hexToBytes(stored.clientSecret),
      ),
      RECONNECT_TIMEOUT_MS,
      "the signer did not answer",
    );
    return sessionFor(signer);
  } catch (e) {
    // The signer may have revoked this browser, or simply be unreachable.
    // Either way the stored connection is no longer usable.
    restoreFailure = reasonFor(e);
    forget();
    return null;
  }
}

/** Why the last restore dropped the stored session. Reading it clears it. */
export function takeRestoreFailure(): string | null {
  const reason = restoreFailure;
  restoreFailure = null;
  return reason;
}

function withTimeout<T>(promise: Promise<T>, ms: number, reason: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${reason} in ${Math.round(ms / 1000)}s`)), ms);
  });
  return Promise.race([promise, expired]).finally(() => clearTimeout(timer));
}

/** nostr-tools rejects with strings and with AggregateError, not only Error. */
function reasonFor(e: unknown): string {
  if (e instanceof AggregateError && e.errors.length > 0) {
    return e.errors.map(reasonFor).join(", ");
  }
  const text = e instanceof Error ? e.message : String(e);
  return text.trim() || "unknown error";
}

export async function signOut(): Promise<void> {
  current?.signer.close();
  current?.relay.close();
  current = null;
  forget();
  // The key is gone, so the decrypted index must go with it.
  await clearAll();
}
